import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config';
import AppRequestsQueue from './AppRequestsQueue';
import ActiveAppsList from './ActiveAppsList';
import AdminCreateAppRequest from './AdminCreateAppRequest';
import useAdminAccess from '../../../context/useAdminAccess';

// Lifecycle stages shown as a connected pipeline. Each stage maps to one or
// more app_requests statuses; `attention` marks stages that need admin action
// (their count badge is emphasized). `rejected` is reachable via the "Rejected"
// chip rather than a pipeline segment (it's an off-track terminal state).
const STAGES = [
  { key: 'requests',     label: 'Requests',     statuses: ['pending'],                     count: ['pending'],                         attention: true,  empty: 'No pending requests.',
    desc: 'New employee app requests awaiting your review. Approve to start provisioning, or reject with a note.' },
  { key: 'provisioning', label: 'Provisioning', statuses: ['approved', 'provisioning'],    count: ['approved', 'provisioning'],        attention: true,  empty: 'No apps are being provisioned right now.',
    desc: 'Approved apps where you attach the secure references (secrets / datasets) each tool needs — including a dedicated Google SSO secret per app.' },
  { key: 'inprogress',   label: 'In progress',  statuses: ['active', 'publish_requested'], count: ['active', 'publish_requested'],     attention: true,  empty: 'No apps in the build / publish-review stage.',
    desc: 'Apps the creator is building (In progress) plus those awaiting your publish review. Review production details, request changes, or publish.' },
  { key: 'published',    label: 'Published',    statuses: ['published'],                   count: ['published'],                       attention: false, empty: 'No apps have been published to the catalog yet.',
    desc: 'Apps already live in the tools catalog. Assign them to departments from the Tools tab like any other tool.' },
];

const REJECTED_DESC = 'Requests you declined. Off-track and terminal — the requester can submit a fresh request addressing your feedback.';

const sumCounts = (stats, keys) => keys.reduce((n, k) => n + (stats[k] || 0), 0);

const STAGE_KEYS = [...STAGES.map((s) => s.key), 'rejected'];
const readStageFromHash = () => {
  const m = (typeof window !== 'undefined' ? window.location.hash : '').match(/appstage=([a-z_]+)/);
  return m && STAGE_KEYS.includes(m[1]) ? m[1] : 'requests';
};

export default function AppsPipeline() {
  const { can } = useAdminAccess();
  const [stats, setStats] = useState(null);
  const [active, _setActive] = useState(readStageFromHash);
  const [createOpen, setCreateOpen] = useState(false);
  const [requestsRefresh, setRequestsRefresh] = useState(0);

  // Persist the selected stage in the URL hash (alongside #tab=apps) so a
  // refresh or shared link reopens the same stage — matching the deep-linking
  // the rest of the admin tabs use.
  const setActive = useCallback((next) => {
    _setActive(next);
    const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    params.set('appstage', next);
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}#${params.toString()}`);
  }, []);

  const mountedRef = useRef(true);
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

  const loadStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/admin/app_requests/stats`, { withCredentials: true });
      // Only accept a well-formed stats object; otherwise leave counts as "—"
      // rather than silently showing 0 for every stage (e.g. a stale server
      // where /stats falls through to the :id route and 400s).
      if (mountedRef.current && res.data && typeof res.data === 'object' && 'pending' in res.data) {
        setStats(res.data);
      }
    } catch {
      /* keep stats null → stages show "—" instead of a misleading 0 */
    }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  const stage = STAGES.find((s) => s.key === active) || STAGES[0];
  const rejectedCount = stats ? (stats.rejected || 0) : 0;
  // After a mutation in a child list, refresh the header counts.
  const onMutated = useCallback(() => { loadStats(); }, [loadStats]);

  return (
    <div className="apps-pipeline-wrap">
      <div className="apps-rail-bar">
        <ol className="apps-rail" aria-label="App lifecycle stages">
          {STAGES.map((s, i) => {
            const count = stats ? sumCounts(stats, s.count) : null;
            const isActive = s.key === active;
            const emphasize = s.attention && count > 0;
            return (
              <li key={s.key} className="apps-rail-item">
                <button
                  type="button"
                  className={`apps-rail-step${isActive ? ' is-active' : ''}${emphasize ? ' has-attention' : ''}`}
                  aria-current={isActive ? 'step' : undefined}
                  aria-label={`${s.label} stage${count == null ? '' : `, ${count} app${count === 1 ? '' : 's'}`}`}
                  onClick={() => setActive(s.key)}
                >
                  <span className="apps-rail-token" aria-hidden="true">
                    {count == null ? '—' : count}
                  </span>
                  <span className="apps-rail-meta">
                    <span className="apps-rail-step-no">Step {i + 1}</span>
                    <span className="apps-rail-label">{s.label}</span>
                    <span className="apps-rail-count">
                      {count == null ? '—' : `${count} app${count === 1 ? '' : 's'}`}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ol>

        <button
          type="button"
          className={`apps-rail-rejected${active === 'rejected' ? ' is-active' : ''}`}
          aria-current={active === 'rejected' ? 'step' : undefined}
          aria-label={`Rejected${stats ? `, ${rejectedCount} app${rejectedCount === 1 ? '' : 's'}` : ''}`}
          onClick={() => setActive('rejected')}
        >
          <span className="apps-rail-rejected-x" aria-hidden="true">✕</span>
          <span className="apps-rail-rejected-text">
            <span className="apps-rail-label">Rejected</span>
            <span className="apps-rail-count">{stats ? `${rejectedCount} app${rejectedCount === 1 ? '' : 's'}` : '—'}</span>
          </span>
        </button>
      </div>

      <div className="apps-pipeline-panel">
        {active === 'rejected' ? (
          <>
            <header className="apps-stage-head">
              <h3 className="apps-stage-title">Rejected</h3>
              <p className="apps-stage-desc">{REJECTED_DESC}</p>
            </header>
            <ActiveAppsList statuses={['rejected']} emptyText="No rejected requests." onCountChange={onMutated} />
          </>
        ) : (
          <>
            <header className="apps-stage-head">
              <div className="apps-stage-head-row">
                <div>
                  <h3 className="apps-stage-title">{stage.label}</h3>
                  <p className="apps-stage-desc">{stage.desc}</p>
                </div>
                {active === 'requests' && can('apps.create_on_behalf') && (
                  <button
                    type="button"
                    className="btn btn-primary apps-stage-action"
                    onClick={() => setCreateOpen(true)}
                  >
                    New request for an employee
                  </button>
                )}
              </div>
            </header>
            {active === 'requests' ? (
              <AppRequestsQueue onCountChange={onMutated} refreshKey={requestsRefresh} />
            ) : (
              <ActiveAppsList statuses={stage.statuses} emptyText={stage.empty} onCountChange={onMutated} />
            )}
          </>
        )}
      </div>

      <AdminCreateAppRequest
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          loadStats();
          setRequestsRefresh((n) => n + 1);
        }}
      />
    </div>
  );
}
